import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/kangaroo.css";
import "./assets/fonts/fonts.css";

// Eagerly load critical components
import ScrollToTop from "./component/ScrollTop/ScrollToTop";
import MainLayout from "./page/Layout/MainLayout";
import GeneralPage from "./pages/GeneralPage/Page/generalPage";
import ScoreCalculator from "./pages/Calculator/page/ScoreCalculator";
import IELTSScoreCalculator from "./pages/Calculator/component/IELTSCalculator";
import PTEScoreCalculator from "./pages/Calculator/component/PTEScoreCalculator";
import NEBGPACalculator from "./pages/Calculator/component/NEBGrade";
import SEEGPACalculator from "./pages/Calculator/component/SEEGPACalculator";
import GPAToPercentageCalculator from "./pages/Calculator/component/GPAtoPercentage";
import PageDetails from "./page/PageTypeTheme/PageDetail";
import ThemeDetail from "./page/Theme/ThemeDetail";
import Privacy from "./pages/PrivacyPage/page/Privacy";
import { RotatingTriangles } from "react-loader-spinner";
import Professional from "./Homepage/Professional";
import SectionContainer from "./Homepage/SectionContainer";
import Testimonial from "./pages/TestimonialPage/page/Testimonial";
import AboutMessage from "./pages/AboutPage/page/AboutMessage";
import { Toaster } from "react-hot-toast";
import Login from "./pages/Authentication/Login";
import ProtectedRoute from "./ProtectedRoute";
import Register from "./pages/Authentication/Register";
import ForgotPassword from "./pages/Authentication/Forgotpassword";
import ResetPassword from "./pages/Authentication/ResetPassword";
import Cover404 from "./common/Cover404";
import ChangePassword from "./pages/Authentication/ChangePassword";
import { PublicAccessProvider } from "./context/PublicAccessContext";
import PasswordGate from "./component/PasswordGate/PasswordGate";

// Lazy load page components
const Homepage = lazy(() => import("./Homepage/Homepage"));
const JoinSuccess = lazy(() => import("./Homepage/JoinSuccess"));
const Page3 = lazy(() => import("./Homepage/LearnMore"));
const About = lazy(() => import("./pages/AboutPage/page/About"));
const TestPreparation = lazy(
  () => import("./pages/TestPreparationPage/page/TestPreparation"),
);
const Ielts = lazy(() => import("./pages/IeltsPage/page/Ielts"));
const InstitutionPage = lazy(
  () => import("./pages/InstitutionPage/page/InstitutionPage"),
);
const CountryPage = lazy(() => import("./pages/CountryPage/page/CountryPage"));
const AboutLanding = lazy(() => import("./pages/AboutPage/page/AboutLanding"));
const InstitutionRepresent = lazy(
  () => import("./pages/InstitutionWeRepresent/page/InstitutionRepresent"),
);
const Destination = lazy(
  () => import("./pages/DestinationPage/page/Destination"),
);
const CareerLanding = lazy(
  () => import("./pages/CareerPage/page/CareerLanding"),
);
const AboutPartner = lazy(() => import("./pages/AboutPage/page/AboutPartner"));
const AboutMilestone = lazy(
  () => import("./pages/AboutPage/page/AboutMilestone"),
);
const CareerDetail = lazy(() => import("./pages/CareerPage/page/CareerDetail"));
const Blog = lazy(() => import("./pages/BlogPage/page/Blog"));
const BlogLanding = lazy(() => import("./pages/BlogPage/page/BlogLanding"));
const Event = lazy(() => import("./pages/EventPage/page/Event"));
const EventDetail = lazy(() => import("./pages/EventPage/page/EventDetail"));
const Franchise = lazy(() => import("./pages/FranchisePage/page/Franchise"));
const Contact = lazy(() => import("./pages/ContactPage/page/Contact"));
const Getfreecounseling = lazy(
  () => import("./pages/GetFreeCounseling/page/Getfreecounseling"),
);
const FranchiseHidden = lazy(
  () => import("./pages/FranchisePage/page/FranchiseHidden"),
);

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
      fontSize: "1.2rem",
      color: "#666",
    }}
  >
    <RotatingTriangles
      visible={true}
      height="80"
      width="80"
      color="#4fa94d"
      ariaLabel="rotating-triangles-loading"
      wrapperStyle={{}}
      wrapperClass=""
    />
  </div>
);

function App() {
  useEffect(() => {
    // Add class to disable all interactions including hover
    document.body.classList.add("no-hover");
    document.body.style.pointerEvents = "none";

    const timer = setTimeout(() => {
      document.body.classList.remove("no-hover");
      document.body.style.pointerEvents = "auto";
    }, 2200);

    return () => {
      clearTimeout(timer);
      document.body.classList.remove("no-hover");
      document.body.style.pointerEvents = "auto";
    };
  }, []);

  // const ProtectedRoute = () => {
  //   const user = localStorage.getItem("user"); // or however you store user data

  //   if (!user) {
  //     return <Navigate to="/login" replace />;
  //   }

  //   return <Outlet />;
  // };
  return (
    <PublicAccessProvider>
            <PasswordGate>

      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* <Route path="/" element={<Login />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} /> */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />


          <Route element={<MainLayout />}>
            {/* STATIC ROUTES FIRST */}
            <Route path="/change-password" element={<ChangePassword />} />

            <Route path="/home" element={<Homepage />} />
            <Route path="/" element={<Homepage />} />

            <Route path="/contact/:id?" element={<Contact />} />
            <Route path="/freecounseling" element={<Getfreecounseling />} />
            <Route path="/about/ceo" element={<About />} />
            <Route
              path="/useful-links/who-we-are"
              element={<AboutLanding />}
            />
            <Route path="/useful-links/partner" element={<AboutPartner />} />
            <Route
              path="/useful-links/milestone"
              element={<AboutMilestone />}
            />

            <Route
              path="/study-abroad/institute-we-represent"
              element={<InstitutionRepresent />}
            />

            <Route path="/useful-links/career" element={<CareerLanding />} />
            <Route
              path="/corporate/career/:jobTitle"
              element={<CareerDetail />}
            />

            {/* <Route path="/test-preparation" element={<TestPreparation />} /> */}

            {/* <Route path="/blog" element={<BlogLanding />} /> */}
            <Route path="/blog/:id" element={<Blog />} />

            <Route path="/events" element={<Event />} />
            <Route path="/events/:eventID" element={<EventDetail />} />
            <Route
              path="/quick-links/testimonial"
            // element={<Testimonial />}
            />

            <Route path="/corporate/franchise" element={<Franchise />} />
            <Route
              path="/corporate/franchisehidden"
              element={<FranchiseHidden />}
            />

            <Route path="/test-preparation/:section" element={<Ielts />} />
            <Route path="/destinations/:section" element={<CountryPage />} />

            <Route
              path="/useful-links/ielts-band-score-calculator"
              element={<IELTSScoreCalculator />}
            />
            <Route
              path="/useful-links/pte-score-calculator"
              element={<PTEScoreCalculator />}
            />
            <Route
              path="/useful-links/neb-to-gpa-calculator"
              element={<NEBGPACalculator />}
            />
            <Route
              path="/useful-links/see-to-gpe-calculator"
              element={<SEEGPACalculator />}
            />
            <Route
              path="/useful-links/gpa-to-percentage-calculator"
              element={<GPAToPercentageCalculator />}
            />

            {/* <Route path="/study-abroad" element={<Destination />} /> */}
            <Route
              path="/destination/:slug?/:id"
              element={<InstitutionPage />}
            />
            <Route
              path="/about/message-from-ceo"
              element={<AboutMessage />}
            />
            <Route
              path="/other-product-services/:slug"
              element={<TestPreparation />}
            />

            {/* ✅ CMS / MENU BASED PAGES — MUST BE LAST */}
            <Route path="/:slug" element={<PageDetails />} />
            <Route path="/privacy/:id" element={<Privacy />} />
            <Route path="/:slug/:page" element={<ThemeDetail />} />
          </Route>

          <Route path="*" element={<Cover404 />} />
        </Routes>
      </Suspense>
      <Toaster />
      </PasswordGate>
    </PublicAccessProvider>
  );
}

export default App;
